// src/view/stage.ts — Renderer, sahne, ışıklar, kalite kademeleri + otomatik uyarlama (ARCHITECTURE §10; viewer/stage.js kalıbı).
// rAF döngüsü game.ts'de (WP9): Stage yalnız `render()` sunar (kare süresi ölçümü + kalite uyarlaması render içinde).
// Ölçüm (viewer, M1, 8 karakter): maliyet ≈ piksel sayısı → antialias:false + pixelRatio kademesi (süperörnekleme MSAA yerine).
import * as THREE from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';
import { T } from '../config/tuning.ts';

export type QualityTier = 'potato' | 'low' | 'medium' | 'high';
export interface QualityDef { label: string; pr: number; env: boolean; shadow: boolean; shadowMap: number }
/** Kademeler ARCHITECTURE §10: dpr 0.75/1/1.5/2, gölge 512/1024/1024/2048, IBL kapalı/kapalı/açık/açık (tuning'de yok → yerel; needsWP0). */
export const QUALITY: Readonly<Record<QualityTier, QualityDef>> = {
  potato: { label: 'En Düşük', pr: 0.75, env: false, shadow: false, shadowMap: 512 },
  low: { label: 'Düşük', pr: 1, env: false, shadow: true, shadowMap: 1024 },
  medium: { label: 'Orta', pr: 1.5, env: true, shadow: true, shadowMap: 1024 },
  high: { label: 'Yüksek', pr: 2, env: true, shadow: true, shadowMap: 2048 },
};
export const TIERS: readonly QualityTier[] = ['potato', 'low', 'medium', 'high'];
/** Otomatik uyarlama eşikleri (ARCHITECTURE §10: fps < 48 → in + tavan; > 58 ve 4 s istikrar → çık). */
const ADAPT_DOWN_FPS = 48;
const ADAPT_UP_FPS = 58;
const ADAPT_HOLD_SEC = 4;
/** Sahne renkleri / sis (viewer/stage.js; görsel tercih, mekanik değil). */
const BG_COLOR = 0x121722;
const FOG_NEAR = 28;
const FOG_FAR = 70;
/** Gölge frustum'u arena kutusuna daraltılmış ve statik (ARCHITECTURE §7 madde 5): yarı genişlik + pay. */
const SHADOW_PAD = 4;
/** ?stats=1 için kare istatistikleri (ARCHITECTURE §10 bütçe tablosu). drawCalls = toplam (gölge geçişi dâhil);
 *  drawCallsShadow = gölge haritası geçişi; drawCallsNoShadow = sahne geçişi (bütçe ≤ 130 buna uygulanır — orkestratör kararı 2). */
export interface StageStats { msRender: number; drawCalls: number; drawCallsShadow: number; drawCallsNoShadow: number; triangles: number; fps: number; tier: QualityTier; dpr: number }

export class Stage {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly hemi: THREE.HemisphereLight;
  readonly keyLight: THREE.DirectionalLight;
  readonly rimLight: THREE.DirectionalLight;
  readonly timer: THREE.Timer;
  readonly stats: StageStats = { msRender: 0, drawCalls: 0, drawCallsShadow: 0, drawCallsNoShadow: 0, triangles: 0, fps: 0, tier: 'medium', dpr: 1 };
  private envMap: THREE.Texture | null = null;
  /** Son karede gölge geçişinin çağrı sayısı (shadowMap.render sarmalayıcısı yazar). */
  private shadowCalls = 0;
  /** Son render'ın dt'si (sn; 0.1 tavan) — kalite uyarlaması ve dış okuma için. */
  dt = 0;
  quality: QualityTier = 'medium';
  autoQuality = true;
  onQualityChange: ((tier: QualityTier, fps: number) => void) | null = null;
  private qTier = 2;
  private qCeil = TIERS.length - 1;
  private qAcc = 0; private qFrames = 0; private qHoldUp = 0;
  private fpsAcc = 0; private fpsFrames = 0;
  private readonly onResize = (): void => this.resize();

  constructor(readonly canvas: HTMLCanvasElement, opts: { quality?: QualityTier; auto?: boolean; env?: boolean } = {}) {
    // antialias:false — MSAA yerine pixelRatio ile süperörnekleme (viewer ölçümü: ~%31 daha ucuz, aynı görsel sonuç).
    this.renderer = new THREE.WebGLRenderer({ canvas, antialias: false, powerPreference: 'high-performance' });
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFShadowMap;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.0;
    this.renderer.info.autoReset = true;
    // Gölge geçişi çağrılarını ayrı say: renderer.render → info.reset → shadowMap.render → sahne geçişi. shadowMap.render örnek
    // özelliği (WebGLShadowMap `this.render = function`) → sarmalanır; önce/sonra info.render.calls farkı = gölge çağrıları (kesin yol).
    const sm = this.renderer.shadowMap;
    const info = this.renderer.info;
    const origShadowRender = sm.render;
    sm.render = (lights, scene, camera): void => {
      const before = info.render.calls;
      origShadowRender.call(sm, lights, scene, camera);
      this.shadowCalls = info.render.calls - before;
    };

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(BG_COLOR);
    this.scene.fog = new THREE.Fog(BG_COLOR, FOG_NEAR, FOG_FAR);

    // Hafif IBL: KayKit düz dokularını canlandırır; düşük kademede kapatılır, hemisphere telafi eder (viewer).
    if (opts.env !== false) {
      const pmrem = new THREE.PMREMGenerator(this.renderer);
      this.envMap = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
      this.scene.environment = this.envMap;
      this.scene.environmentIntensity = 0.35;
      pmrem.dispose();
    }

    // Kamera: FOV sabit (DESIGN §10: zoom = mesafe); aspect resize'da; near/far arena ölçeğine (mesafe ≤ 28 u).
    this.camera = new THREE.PerspectiveCamera(T.CAMERA.fovDeg, 1, 0.1, 200);
    this.camera.position.set(0, 12, 22);
    this.camera.lookAt(0, 0, 0);

    this.hemi = new THREE.HemisphereLight(0xbcd4ff, 0x2b2417, 1.15);
    this.scene.add(this.hemi);
    this.keyLight = new THREE.DirectionalLight(0xfff2dd, 2.2);
    this.keyLight.position.set(8, 16, 10);
    this.keyLight.castShadow = true;
    this.keyLight.shadow.bias = -0.0009;
    this.keyLight.shadow.normalBias = 0.02;
    this.scene.add(this.keyLight);
    this.scene.add(this.keyLight.target);
    this.rimLight = new THREE.DirectionalLight(0x6ea8ff, 1.0);
    this.rimLight.position.set(-6, 4, -7);
    this.scene.add(this.rimLight);

    this.timer = new THREE.Timer();
    if (typeof document !== 'undefined') this.timer.connect(document);

    this.autoQuality = opts.auto !== false;
    const q = opts.quality ?? 'medium';
    this.qTier = Math.max(0, TIERS.indexOf(q));
    this.applyQuality(q);
    if (!this.autoQuality) this.qCeil = this.qTier;

    addEventListener('resize', this.onResize);
    this.resize();
  }

  /* ------------------------------------------------------------- kalite */

  applyQuality(name: QualityTier): QualityDef {
    const q = QUALITY[name];
    const envChanged = (this.scene.environment !== null) !== (q.env && this.envMap !== null);
    const shadowChanged = this.renderer.shadowMap.enabled !== q.shadow;
    this.quality = name;
    this.stats.tier = name;
    const dpr = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, q.pr);
    this.stats.dpr = dpr;
    this.renderer.setPixelRatio(dpr);
    this.scene.environment = q.env ? this.envMap : null;
    this.hemi.intensity = this.scene.environment ? 1.15 : 1.55;   // IBL kapalıyken telafi
    this.renderer.shadowMap.enabled = q.shadow;
    // Gölge haritası yalnız gölge açıkken yeniden boyutlandırılır (kapalıyken map'i null'a çekmek boş sampler bağlıyor — viewer).
    const s = this.keyLight.shadow;
    if (q.shadow && s.mapSize.x !== q.shadowMap) {
      s.map?.dispose();
      s.map = null;
      s.mapSize.set(q.shadowMap, q.shadowMap);
    }
    // env / gölge açılıp kapanınca shader yeniden derlenmeli; aksi hâlde var olmayan sampler örneklenir (GL_INVALID_OPERATION).
    if (envChanged || shadowChanged) {
      this.scene.traverse((o) => {
        const m = (o as THREE.Mesh).material as THREE.Material | THREE.Material[] | undefined;
        if (!m) return;
        if (Array.isArray(m)) for (const mm of m) mm.needsUpdate = true; else m.needsUpdate = true;
      });
    }
    this.resize();
    return q;
  }

  /** El ile seçim: kademeyi kilitler (menü "kalite" seçimi tavanı kilitler — ARCHITECTURE §10). */
  setQuality(name: QualityTier, lock = true): void {
    this.qTier = Math.max(0, TIERS.indexOf(name));
    this.applyQuality(name);
    if (lock) { this.autoQuality = false; this.qCeil = this.qTier; }
  }
  setAutoQuality(on: boolean): void {
    this.autoQuality = on;
    this.qAcc = 0; this.qFrames = 0; this.qHoldUp = 0;
    if (on) this.resetQualityCeiling();
  }
  /** Sahne ağırlığı değişince (menü ↔ maç) tavan serbest kalır. */
  resetQualityCeiling(): void { this.qCeil = TIERS.length - 1; }

  private adapt(dt: number): void {
    this.qAcc += dt; this.qFrames++;
    if (this.qAcc < 1) return;
    const fps = this.qFrames / this.qAcc;
    this.qAcc = 0; this.qFrames = 0;
    if (fps < ADAPT_DOWN_FPS && this.qTier > 0) {
      // Bu kademe yetmedi: bir daha otomatik buraya tırmanma (çık-düş-çık salınımı önlenir).
      this.qCeil = this.qTier - 1;
      this.qTier--; this.qHoldUp = 0;
      this.applyQuality(TIERS[this.qTier]!);
      this.onQualityChange?.(TIERS[this.qTier]!, Math.round(fps));
    } else if (fps > ADAPT_UP_FPS && this.qTier < this.qCeil) {
      if (++this.qHoldUp >= ADAPT_HOLD_SEC) {
        this.qHoldUp = 0; this.qTier++;
        this.applyQuality(TIERS[this.qTier]!);
        this.onQualityChange?.(TIERS[this.qTier]!, Math.round(fps));
      }
    } else {
      this.qHoldUp = 0;
    }
  }

  /* --------------------------------------------------------------- sahne */

  /** Gölge kamerasını verilen yarı genişliğe daraltır; arena için bir kez çağrılır (statik, ARCHITECTURE §7). */
  setShadowExtent(halfSize: number, center?: { x: number; y: number; z: number }): void {
    const c = this.keyLight.shadow.camera;
    const h = Math.max(halfSize, 2);
    c.left = -h; c.right = h; c.top = h; c.bottom = -h;
    c.near = 0.5; c.far = h * 3 + 20;
    c.updateProjectionMatrix();
    if (center) {
      const reach = h * 1.6 + 10;
      this.keyLight.position.set(center.x + reach * 0.45, center.y + reach * 0.9, center.z + reach * 0.55);
      this.keyLight.target.position.set(center.x, center.y, center.z);
      this.keyLight.target.updateMatrixWorld();
    }
    this.keyLight.shadow.needsUpdate = true;
  }
  /** Arena kutusuna göre gölge + sis ayarı (arenaView kurulunca WP9 çağırır). */
  fitArena(halfWidth: number, center: { x: number; y: number; z: number }): void {
    this.setShadowExtent(halfWidth + SHADOW_PAD, center);
    const fog = this.scene.fog as THREE.Fog | null;
    if (fog) { fog.near = Math.max(FOG_NEAR, T.CAMERA.maxDist + 6); fog.far = T.CAMERA.maxDist * 3 + 20; }
  }

  resize(): void {
    const w = this.canvas.clientWidth || 1;
    const h = this.canvas.clientHeight || 1;
    this.renderer.setSize(w, h, false);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
  }

  /* --------------------------------------------------------------- çizim */

  /** Bir kare çizer; dt ölçer (Timer), istatistikleri günceller, kalite uyarlar. game.ts her rAF'ta çağırır. */
  render(): void {
    this.timer.update();
    const dt = Math.min(this.timer.getDelta(), 0.1);
    this.dt = dt;
    const t0 = performance.now();
    // Gölge kapalı kademede (potato / applyQuality) shadowMap.render hiç çağrılmaz → sarmalayıcı yazmaz, son gölgeli karenin değeri
    // bayat kalırdı (drawCallsNoShadow yanlış/negatif). Her karede sıfırla; gölge geçişi çalışırsa üstüne yazar.
    this.shadowCalls = 0;
    this.renderer.render(this.scene, this.camera);
    this.stats.msRender = performance.now() - t0;
    this.stats.drawCalls = this.renderer.info.render.calls;
    this.stats.drawCallsShadow = this.shadowCalls;
    this.stats.drawCallsNoShadow = this.stats.drawCalls - this.shadowCalls;
    this.stats.triangles = this.renderer.info.render.triangles;
    this.fpsAcc += dt; this.fpsFrames++;
    if (this.fpsAcc >= 0.5) {
      this.stats.fps = Math.round(this.fpsFrames / this.fpsAcc);
      this.fpsAcc = 0; this.fpsFrames = 0;
    }
    if (this.autoQuality) this.adapt(dt);
  }

  dispose(): void {
    removeEventListener('resize', this.onResize);
    this.timer.disconnect();
    this.envMap?.dispose();
    this.renderer.dispose();
  }
}
